import React, {useEffect, useState, useCallback} from 'react'
import {Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup, Spinner} from 'reactstrap'

/**
 * Modal for saving a custom shape to the server.
 * Collects shape name (required) and description (optional).
 *
 * @param {{
 * initialData?: {
 *   name?: string,
 *   description?: string,
 * }
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onConfirm: (name: string, description: string) => Promise<void>
 * }} props
 */
function SaveShapeModal({initialData, isOpen, onClose, onConfirm}) {
    const [name, setName] = useState(initialData?.name ?? '')
    const [description, setDescription] = useState(initialData?.description ?? '')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        setName(initialData?.name)
        setDescription(initialData.description)
    }, [initialData])

    const handleConfirm = useCallback(async () => {
        const trimmedName = name.trim()
        if (!trimmedName) return

        setSaving(true)
        setError(null)

        try {
            await onConfirm(trimmedName, description?.trim())
            // Reset form on success (modal will close via parent)
            setName('')
            setDescription('')
        } catch (err) {
            setError(err?.message || 'Failed to save shape. Please try again.')
        } finally {
            setSaving(false)
        }
    }, [name, description, onConfirm])

    const handleClose = useCallback(() => {
        if (saving) return
        setError(null)
        onClose()
    }, [saving, onClose])

    const canConfirm = name?.trim().length > 0 && !saving

    return (
        <Modal isOpen={isOpen} toggle={handleClose} className="SaveShapeModal">
            <ModalHeader toggle={handleClose}>Save Shape</ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label for="save-shape-name">Name *</Label>
                    <Input
                        id="save-shape-name"
                        type="text"
                        placeholder="Enter shape name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={saving}
                        autoFocus
                    />
                </FormGroup>
                <FormGroup>
                    <Label for="save-shape-description">Description</Label>
                    <Input
                        id="save-shape-description"
                        type="textarea"
                        placeholder="Enter description (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={saving}
                        rows={3}
                    />
                </FormGroup>
                {error && (
                    <p className="SaveShapeModal__Error text-danger" role="alert">
                        {error}
                    </p>
                )}
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={saving}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={!canConfirm}>
                    {saving ? (
                        <>
                            <Spinner size="sm" /> Saving…
                        </>
                    ) : (
                        'Save'
                    )}
                </button>
            </ModalFooter>
        </Modal>
    )
}

export default SaveShapeModal
